import { Add, ArrowForward, ExpandMoreOutlined } from "@mui/icons-material";
import {
    Accordion,
    AccordionDetails,
    Box,
    FormControlLabel,
    IconButton,
    Typography,
} from "@mui/material";
import { chain } from "lodash";
import { Fragment, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import EpcClassificationsCell from "components/Cells/EpcClassificationsCell";
import { EmptyState } from "legacyNext/components";
import { ReductionProposal } from "legacyNext/types";

import { S } from "./reduction-plan.styles";
import { ReductionModal } from "./ReductionModal";
import { ReductionPlanTable } from "./ReductionPlanTable";

type Props = {
    proposals: Array<ReductionProposal>;
    onAdd: (proposal: Partial<ReductionProposal>) => void;
    onChange: (
        id: string,
        key: "startYear" | "enabled",
        value: null | number | boolean
    ) => void;
};

export const ReductionPlanView = (props: Props) => {
    const { proposals, onAdd, onChange } = props;

    const { t } = useTranslation();

    const [open, setOpen] = useState(false);
    const [displayProfitableOnly, setDisplayProfitableOnly] = useState(false);

    const title = useMemo(() => {
        const epcCount = new Set(proposals.map((proposal) => proposal.epc))
            .size;

        return `${t("legacyNext.reductionPlan", "reduction plan")} - ${
            proposals.length
        } ${t("legacyNext.initiative", "initiative", {
            count: proposals.length,
        })} - ${epcCount} EPC`;
    }, [proposals, t]);

    const groupedProposals = useMemo(
        () =>
            chain(proposals)
                .filter(
                    (proposal) =>
                        !displayProfitableOnly ||
                        proposal.category === "Profitable"
                )
                .sortBy((p) => Number(p.id))
                .groupBy("epc")
                .values()
                .map((proposals) => proposals)
                .value(),
        [displayProfitableOnly, proposals]
    );

    const proposalTables = useMemo(
        () =>
            groupedProposals.map((proposals, index) => {
                const { labels, epc, address } = proposals[0];
                const count = `${proposals.length} ${t(
                    "legacyNext.initiative",
                    "initiative",
                    { count: proposals.length }
                )}`;

                const targetLabel = displayProfitableOnly
                    ? labels.profitable
                    : labels.possible;
                const classifications = (
                    <S.Classifications>
                        {EpcClassificationsCell([labels.current])}
                        <ArrowForward sx={{ fontSize: 18 }} />
                        {EpcClassificationsCell([targetLabel])}
                    </S.Classifications>
                );
                const summaryItems = [count, address, epc];

                return (
                    <Accordion key={index} defaultExpanded={index === 0}>
                        <S.Summary expandIcon={<ExpandMoreOutlined />}>
                            {classifications}
                            {summaryItems.map((text, i) => (
                                <Fragment key={i}>
                                    <Typography variant="h3">·</Typography>
                                    <Typography>{text}</Typography>
                                </Fragment>
                            ))}
                        </S.Summary>
                        <AccordionDetails>
                            <ReductionPlanTable
                                proposals={proposals}
                                onChange={onChange}
                            />
                        </AccordionDetails>
                    </Accordion>
                );
            }),
        [displayProfitableOnly, groupedProposals, onChange, t]
    );

    const onChangeDisplayProfitableOnly = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            chain(proposals)
                .filter((proposal) => proposal.category !== "Profitable")
                .forEach((proposal) => {
                    onChange(proposal.id, "enabled", false);
                    onChange(proposal.id, "startYear", null);
                })
                .value();
            setDisplayProfitableOnly(event.target.checked);
        },
        [onChange, proposals]
    );

    return (
        <>
            <ReductionModal
                open={open}
                handleClose={() => setOpen(false)}
                handleAddProposal={onAdd}
            />
            <S.Header>
                <Typography
                    variant="h2"
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                    }}
                >
                    {title}

                    <IconButton onClick={() => setOpen(true)}>
                        <Add fontSize="small" />
                    </IconButton>
                </Typography>
                <Typography maxWidth="60%" variant="body1">
                    {t(
                        "legacyNext.reductionPlanSubtitle",
                        "to get started with simulating a reduction plan, choose a year for your analysis to start from. then, select an initiative from the list to see how the property's footprint is affected in the graph above."
                    )}
                </Typography>
            </S.Header>

            {proposals.length ? (
                <Box ml={4}>
                    <FormControlLabel
                        control={
                            <S.Switch
                                checked={displayProfitableOnly}
                                onChange={onChangeDisplayProfitableOnly}
                            />
                        }
                        label={t(
                            "legacyNext.profitableOnly",
                            "profitable only"
                        )}
                    />

                    <Typography variant="body2" ml={10}>
                        {t(
                            "legacyNext.profitableOnlyDescription",
                            "you can either see only profitable proposals or all proposals."
                        )}
                    </Typography>
                </Box>
            ) : (
                <EmptyState title={t("legacyNext.noData", "no data")} />
            )}

            {proposalTables}
        </>
    );
};
